import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components';
import type { ScenarioConfig, ScenarioCredential, ScenarioIssue, ScenarioTool, SystemToolMeta } from './types';
import { isSystemTool, moveItem, newSystemTool, newTool, removeAt, setIn } from './scenarioDraft';
import { ConnectionsEditor } from './ConnectionsEditor';
import { FactsEditor } from './FactsEditor';
import { ToolEditor } from './ToolEditor';
import { AddButton, IssueList, RowActions, Section, TextField } from './ui';

interface Props {
  config: ScenarioConfig;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
  credentials: ScenarioCredential[];
  systemTools?: SystemToolMeta[];
  enabledSystemTools?: string[];
  // Tool to open and scroll to when the editor mounts (e.g. coming from the flow view).
  initialOpenTool?: number | null;
}

const toolAnchor = (index: number) => `scenario-tool-${index}`;

export const ScenarioFormEditor: React.FC<Props> = ({
  config,
  onChange,
  issues,
  credentials,
  systemTools = [],
  enabledSystemTools,
  initialOpenTool = null,
}) => {
  const { t } = useTranslation();
  const [addedIndex, setAddedIndex] = useState<number | null>(null);
  useEffect(() => {
    if (initialOpenTool === null) return;
    document.getElementById(toolAnchor(initialOpenTool))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [initialOpenTool]);
  const addTool = (tool: ScenarioTool) => {
    setAddedIndex(config.tools.length);
    onChange({ ...config, tools: [...config.tools, tool] });
  };
  return (
    <div className="space-y-4" data-testid="scenario-form-editor">
      <IssueList issues={issues} pointer="" />
      <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 md:grid-cols-3">
        <TextField
          label={t('agents.scenario.name')}
          value={config.name}
          onChange={value => onChange(setIn(config, ['name'], value))}
          issues={issues}
          pointer="/name"
        />
        <div className="md:col-span-2">
          <TextField
            label={t('agents.scenario.purpose')}
            value={config.description}
            onChange={value => onChange(setIn(config, ['description'], value))}
            issues={issues}
            pointer="/description"
            multiline
          />
        </div>
      </div>

      <Section title={t('agents.scenario.connections')} icon="link" issues={issues} pointer="/connections">
        <ConnectionsEditor config={config} onChange={onChange} issues={issues} credentials={credentials} />
      </Section>

      <Section
        title={t('agents.scenario.facts')}
        icon="fact_check"
        subtitle={t('agents.scenario.factsHint')}
        issues={issues}
        pointer="/facts"
      >
        <FactsEditor config={config} onChange={onChange} issues={issues} />
      </Section>

      <Section title={t('agents.scenario.tools')} icon="build" issues={issues} pointer="/tools">
        <IssueList issues={issues} pointer="/tools" />
        {config.tools.map((tool, index) => (
          <Section
            key={index}
            id={toolAnchor(index)}
            title={tool.title || tool.id}
            badges={
              <>
                {isSystemTool(tool) ? (
                  <Badge variant="accent" size="sm">
                    {t('agents.scenario.systemTools.badge')}
                  </Badge>
                ) : (
                  <Badge variant={tool.operation === 'write' ? 'warning' : 'info'} size="sm">
                    {t(tool.operation === 'write' ? 'agents.scenario.operationWrite' : 'agents.scenario.operationRead')}
                  </Badge>
                )}
                {!tool.enabled && (
                  <Badge variant="secondary" size="sm">
                    {t('agents.scenario.disabled')}
                  </Badge>
                )}
              </>
            }
            subtitle={`sc_${tool.id}`}
            issues={issues}
            pointer={`/tools/${index}`}
            defaultOpen={index === addedIndex || index === initialOpenTool}
            actions={
              <RowActions
                index={index}
                count={config.tools.length}
                onMove={(from, to) => onChange({ ...config, tools: moveItem(config.tools, from, to) })}
                onRemove={() => onChange({ ...config, tools: removeAt(config.tools, index) })}
              />
            }
          >
            <ToolEditor
              config={config}
              index={index}
              onChange={onChange}
              issues={issues}
              systemTools={systemTools}
              enabledSystemTools={enabledSystemTools}
            />
          </Section>
        ))}
        <div className="flex flex-wrap gap-2">
          <AddButton label={t('agents.scenario.addTool')} onClick={() => addTool(newTool(config))} />
          {systemTools.length > 0 && (
            <AddButton
              label={t('agents.scenario.systemTools.add')}
              onClick={() => addTool(newSystemTool(config, systemTools[0]))}
            />
          )}
        </div>
      </Section>
    </div>
  );
};
